import type { ReactNode } from "react";
import { CircleHelp, ListChecks, Lock } from "lucide-react";
import { buttonChrome } from "@/components/ui/button";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { NavLink, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { CountMark } from "./CountMark";
import { MaybeLink } from "./MaybeLink";
import {
  V2_STAGES,
  openQuestionsPath,
  railFooterLabel,
  stagePath,
  stepDestination,
  type SubStepGroup,
  type SubStepId,
  type V2Stage,
  type V2StageId,
} from "@/v2/stage-vocabulary";

// The active stage is a pill inset from the rail's edges, as the design draws it.
const ITEM_CLASS =
  "mx-2 flex items-center gap-2.5 rounded-[9px] px-3 py-2 text-[13px] transition-colors";
// Mono on the chip is the wireframe's `.pn` rule.
const CHIP_CLASS =
  "grid size-5 shrink-0 place-items-center rounded-[5px] font-mono text-[10.5px] font-semibold";

/**
 * The boxed buttons at the rail's foot: the conventions, the legal documents
 * and the change log, each a box of its own as the design draws them.
 */
export const RAIL_BOX = cn(
  buttonChrome({ variant: "shell" }),
  "h-10 w-full justify-start gap-2.5 rounded-[9px] px-3 text-left text-[13px] font-medium",
);

/** What the rail's marks key on: a stage's steps and the stages themselves, whose ids never overlap. */
export type RailDone = Readonly<
  Partial<Record<SubStepId | V2StageId, boolean>>
>;

/** Which stages are shut behind the stage gate, as the shell reads it. */
export type RailLocked = Readonly<Partial<Record<V2StageId, boolean>>>;

/** The changes waiting on each stage or step, by its id, as v0.28's rail counts them. */
export type RailCounts = Readonly<
  Partial<Record<V2StageId | SubStepId, number>>
>;

/** The words a step reads in place of its vocabulary label, by step. */
export type RailLabels = Readonly<Partial<Record<SubStepId, string>>>;

/** Whether Praxis has asked anything yet, and how many of its questions are still open. */
export interface RailQuestions {
  asked: boolean;
  open: number;
}

const NOT_YET_ASKED = "Available once Praxis has asked its first questions";

/**
 * Open Questions, below the stages after a rule: greyed until Praxis has asked,
 * then a way to the page with a red count of the questions still open. The page
 * is told where the attorney came from, so its bar can take them back.
 */
function QuestionsItem({
  taskId,
  questions,
}: {
  taskId: string | undefined;
  questions: RailQuestions;
}) {
  const here = useLocation();
  const body = (isActive: boolean) => (
    <>
      <span
        className={cn(
          CHIP_CLASS,
          isActive
            ? "bg-white/25 text-white"
            : "bg-sidebar-accent text-white/60",
          "[&_svg]:size-[13px]",
        )}
      >
        <CircleHelp aria-hidden />
      </span>
      <span className="truncate">Open Questions</span>
      {questions.open > 0 && (
        <CountMark count={questions.open} noun="open question" />
      )}
    </>
  );
  const row =
    taskId === undefined || !questions.asked ? (
      <div title={NOT_YET_ASKED} className={cn(ITEM_CLASS, "text-white/35")}>
        {body(false)}
      </div>
    ) : (
      <NavLink
        to={openQuestionsPath(taskId)}
        // Pressed again on the page itself, it keeps where the attorney first came from.
        state={
          here.pathname === openQuestionsPath(taskId)
            ? (here.state as unknown)
            : { from: here.pathname }
        }
        className={({ isActive }) =>
          cn(
            ITEM_CLASS,
            isActive
              ? "bg-brand-mid font-semibold text-white"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white",
          )
        }
      >
        {({ isActive }) => body(isActive)}
      </NavLink>
    );
  // After a rule, in a list of its own: it is no stage.
  return (
    <>
      <hr className="mx-4 my-2.5 border-sidebar-border" />
      <ul className="flex flex-col gap-px">
        <li>{row}</li>
      </ul>
    </>
  );
}

const LOCKED_WHY =
  "Locked until an element, a position and a novelty are confirmed";

/**
 * The lock a shut stage wears at the right of its row. The row stays a link:
 * the stage's page says what opens it.
 */
function LockMark() {
  return (
    <span title={LOCKED_WHY} className="ml-auto flex-none text-white/70">
      <Lock aria-hidden className="size-3" />
      <span className="sr-only">Locked</span>
    </span>
  );
}

/** The tick a finished step or stage wears in place of its number, saying what it means as well as showing it. */
function DoneMark({ testId }: { testId: string }) {
  return (
    <span data-testid={testId}>
      <span aria-hidden>✓</span>
      <span className="sr-only">Done</span>
    </span>
  );
}

/**
 * One rail row.
 *
 * With no draft open the row is a non-interactive `div`: the stage routes are
 * all draft-scoped, so a link here would have nowhere to point.
 */
function StageItem({
  stage,
  number,
  taskId,
  finished = false,
  locked = false,
  count = 0,
  children,
}: {
  stage: V2Stage;
  /** Where the stage stands in the rail, counted from one. */
  number: number;
  taskId: string | undefined;
  /** The attorney is past this stage and is not reading it. */
  finished?: boolean;
  /** The stage gate holds this stage shut. */
  locked?: boolean;
  /** The changes waiting on the stage, drawn while its steps are not. */
  count?: number;
  children?: ReactNode;
}) {
  const body = (isActive: boolean) => (
    <>
      <span
        data-testid={`v2-stage-chip-${stage.id}`}
        className={cn(
          CHIP_CLASS,
          finished
            ? "bg-success text-white"
            : isActive
              ? "bg-white/25 text-white"
              : "bg-sidebar-accent text-white/60",
        )}
      >
        {finished ? <DoneMark testId={`v2-stage-done-${stage.id}`} /> : number}
      </span>
      <span className="truncate">{stage.label}</span>
      {count > 0 && <CountMark count={count} />}
      {locked && <LockMark />}
    </>
  );

  if (taskId === undefined) {
    return (
      <li>
        <div className={cn(ITEM_CLASS, "text-white/35")}>{body(false)}</div>
        {children}
      </li>
    );
  }

  return (
    <li>
      {/* NavLink sets aria-current="page" from isActive, so exactly one row is
          marked without the rail tracking the active stage itself. */}
      <NavLink
        to={stagePath(taskId, stage.id)}
        className={({ isActive }) =>
          cn(
            ITEM_CLASS,
            isActive
              ? "bg-brand-mid font-semibold text-white"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white",
          )
        }
      >
        {({ isActive }) => body(isActive)}
      </NavLink>
      {/* The sub-steps belong to this row, so they hang inside its list item: a
          list may hold only list items, and a screen reader reads a nested list
          as part of the item it sits in. */}
      {children}
    </li>
  );
}

/**
 * A stage's own steps, shown only while that stage is open.
 *
 * An unbuilt step is not a link, since a link would point at a route that
 * resolves to nothing. `MaybeLink` decides how that reads, here and in the
 * two other places the workflow lists a destination before it answers.
 *
 * A step the attorney has finished carries a mark, except the one they are
 * reading: a page is not finished by being open, which is the rule the step
 * strip above the content already follows. The mark says what it means rather
 * than showing a glyph alone, so it reaches a reader who cannot see it.
 */
function SubStepList({
  taskId,
  stageId,
  group,
  activeId,
  done,
  labels,
  counts,
}: {
  taskId: string | undefined;
  stageId: V2StageId;
  group: SubStepGroup;
  activeId: SubStepId | undefined;
  done: RailDone;
  labels: RailLabels;
  counts: RailCounts;
}) {
  return (
    <ul className="mb-1 ml-[26px] mr-2 mt-1 flex flex-col gap-0.5 border-l border-white/15 pl-1.5">
      {group.steps.map((step, at) => {
        const reading = step.id === activeId;
        const finished = step.built && done[step.id] === true && !reading;
        const count = counts[step.id] ?? 0;
        const row = (
          <span
            data-testid={`v2-step-row-${step.id}`}
            className={cn(
              "flex items-center gap-2.5 rounded-[8px] px-2 py-[7px] text-[12px]",
              reading
                ? "bg-sidebar-accent font-semibold text-white"
                : "text-sidebar-foreground",
              step.built ? "" : "opacity-50",
            )}
          >
            <span
              data-testid={`v2-step-mark-${step.id}`}
              className={cn(
                "grid size-[18px] shrink-0 place-items-center rounded-full text-[10px] font-semibold",
                finished
                  ? "bg-success text-white"
                  : reading
                    ? "bg-brand-start text-white"
                    : "bg-sidebar-accent text-white/60",
              )}
            >
              {/* The number is the list's order, which a reader already
                  hears; the tick says something the words do not. */}
              {finished ? (
                <DoneMark testId={`v2-substep-done-${step.id}`} />
              ) : (
                <span aria-hidden>{at + 1}</span>
              )}
            </span>
            <span data-testid={`v2-substep-${step.id}`}>
              {labels[step.id] ?? step.label}
            </span>
            {count > 0 && <CountMark count={count} />}
          </span>
        );

        const to = stepDestination(step, taskId, stageId);
        return (
          <li key={step.id}>
            <MaybeLink to={to}>{row}</MaybeLink>
          </li>
        );
      })}
    </ul>
  );
}

/** A stage open on its steps counts on the step instead, so nothing is counted twice. */
function stageCount(
  stage: V2Stage,
  activeStageId: V2StageId | undefined,
  counts: RailCounts,
): number {
  return stage.id === activeStageId && stage.subSteps !== undefined
    ? 0
    : (counts[stage.id] ?? 0);
}

/** Every stage in one numbered list, the open one with its steps. */
function StageList({
  taskId,
  activeStageId,
  activeSubStepId,
  done,
  locked,
  labels,
  counts,
}: {
  taskId: string | undefined;
  activeStageId: V2StageId | undefined;
  activeSubStepId: SubStepId | undefined;
  done: RailDone;
  locked: RailLocked;
  labels: RailLabels;
  counts: RailCounts;
}) {
  return (
    <ul className="flex flex-col gap-px">
      {V2_STAGES.map((stage, at) => (
        <StageItem
          key={stage.id}
          stage={stage}
          number={at + 1}
          taskId={taskId}
          finished={done[stage.id] === true && stage.id !== activeStageId}
          locked={locked[stage.id] === true}
          count={stageCount(stage, activeStageId, counts)}
        >
          {/* Only the open stage shows its steps, so a rail with two
              stepped stages never shows two runs of steps at once. */}
          {stage.id === activeStageId && stage.subSteps !== undefined && (
            <SubStepList
              taskId={taskId}
              stageId={stage.id}
              group={stage.subSteps}
              activeId={activeSubStepId}
              done={done}
              labels={labels}
              counts={counts}
            />
          )}
        </StageItem>
      ))}
    </ul>
  );
}

/**
 * The stage rail: every stage in one numbered list, then the position footer,
 * then whatever the shell hangs below it, then the change log.
 *
 * The draft control arrives as `children` rather than an import so the rail
 * stays presentational — it holds no data hooks of its own.
 *
 * It takes both of the shell's content rows. The notice along the bottom is the
 * working area's, not the whole window's, so the rail runs past it to the floor.
 */
export function V2StageRail({
  taskId,
  activeStageId,
  activeSubStepId,
  done = {},
  locked = {},
  labels = {},
  counts = {},
  questions,
  children,
}: {
  taskId?: string;
  activeStageId?: V2StageId;
  activeSubStepId?: SubStepId;
  /** Which steps and stages the attorney has finished, as the shell reads it: one map for both. */
  done?: RailDone;
  /** Which stages the stage gate holds shut. */
  locked?: RailLocked;
  /** A step's words where the matter's state changes them; the vocabulary's elsewhere. */
  labels?: RailLabels;
  /** The changes waiting on each stage or step (lane M decision 21). */
  counts?: RailCounts;
  /** Praxis's questions, for the Open Questions item; none drawn without them. */
  questions?: RailQuestions;
  children?: ReactNode;
}) {
  const footer = railFooterLabel(activeStageId, activeSubStepId);

  return (
    <aside
      data-testid="v2-stage-rail"
      className="row-span-2 flex min-h-0 flex-col border-r border-sidebar-border bg-sidebar"
    >
      <nav className="min-h-0 flex-1 overflow-y-auto pb-4">
        <MicroLabel
          as="h2"
          size="sm"
          className="px-4 pb-2 pt-4 text-sidebar-foreground"
        >
          Planning
        </MicroLabel>
        <StageList
          taskId={taskId}
          activeStageId={activeStageId}
          activeSubStepId={activeSubStepId}
          done={done}
          locked={locked}
          labels={labels}
          counts={counts}
        />
        {questions !== undefined && (
          <QuestionsItem taskId={taskId} questions={questions} />
        )}
      </nav>
      {footer !== null && (
        <p
          data-testid="v2-rail-footer"
          className="border-t border-sidebar-border px-4 py-3 text-[11px] text-sidebar-foreground"
        >
          {footer}
        </p>
      )}
      <div className="flex flex-col gap-2 px-2 pb-3">
        {children}
        <MaybeLink to="/changelog" className={RAIL_BOX}>
          <ListChecks aria-hidden />
          Change Log
        </MaybeLink>
      </div>
    </aside>
  );
}
